import { useMemo, useState, useEffect } from 'react';
import loadable from '@loadable/component';
import { Column, Button } from '@simplywallst/ui-core';
import Statement from '@/components/Statement';
import Description from '@/components/Statement/components/Description';
import type { BalanceSheetHierarchyNode } from '../BalanceSheet';
import ReportSection from '@/components/ReportSection';
import ReportSubSection from '@/components/ReportSubSection';
import track, { useTracking } from 'react-tracking';
import {
  COMPANY_DEBT_TO_EQUITY_HISTORY,
  COMPANY_FINANCIAL_POSITION_ANALYSIS,
} from '@/constants/feedbackSections';
import { ChartType } from '@/constants/downloadSections';
import { getCompanyInfographicDownloadUrl } from '@utilities/urlGenerator';
import {
  HEALTH_BALANCE_SHEET,
  HEALTH_CASH_RUNWAY,
  HEALTH_DEBT_HISTORY,
  HEALTH_FINANCIAL_INSTITUTION,
  HEALTH_FINANCIAL_POSITION,
  LegacyAnchor,
} from '@/constants/anchors';
import { CompanyStatements } from '@/constants/statementIds';
import PageAnchor from '@/components/PageAnchor';
/** Raw Data Containers */
import { BalanceSheetWrapper } from './styled';
import { BrazeContentCardWrapper } from '@/pages/CompanyReport/components/BrazeContentCard';
import CompanySectionTitle from '@/pages/CompanyReport/components/CompanySectionTitle';
import ChartWrapper from '@/pages/CompanyReport/components/Overview/components/ChartWrapper';
import { useVisibleSectionsIsPristine } from '../../../VisibleSectionsContextProvider';
import useCompanyStatementsAPIFormatted from '@/hooks/useCompanyStatementsAPIFormatted';
import { useCompanyCanonicalUrl } from '@/hooks/useCompany';
import Header from '../Header';
import {
  useHealthTranslations,
  HealthTranslation,
} from '../../hooks/useHealthTranslations';

const BrazeContentCard = loadable(() => import('@components/BrazeContentCard'));

const BalanceSheet = loadable(() => import('../BalanceSheet'), { ssr: false });

const DebtToEquityHistoryRawDataContainer = loadable(
  () => import('../../containers/DebtToEquityHistoryRawDataContainer'),
  { ssr: false }
);

const DebtToEquityHistory = loadable(() => import('../DebtToEquityHistory'), {
  ssr: false,
});
const AssetsVsLiabilities = loadable(() => import('../AssetsVsLiabilities'), {
  ssr: false,
});

const NextStep = loadable(
  () => import('@/pages/CompanyReport/components/SimilarCompanies/NextStep')
);

export interface Props {
  companyId: string;
  companyName: string;
  isFund: boolean;
  longTermAssets: number;
  longTermAssetsFormatted0DP: string;
  longTermAssetsFormatted2DP: string;
  longTermLiabilities: number;
  longTermLiabilitiesFormatted0DP: string;
  longTermLiabilitiesFormatted2DP: string;
  reportingCurrencyISO: string;
  reportingUnit: number;
  sectionNumber: number;
  shortTermAssets: number;
  shortTermAssetsFormatted0DP: string;
  shortTermAssetsFormatted2DP: string;
  shortTermLiabilities: number;
  shortTermLiabilitiesFormatted0DP: string;
  shortTermLiabilitiesFormatted2PD: string;
  /**
   * Debt To Equity History
   */
  debt: number[][];
  equity: number[][];
  cash: number[][];
  debtMax: number;
  equityMax: number;
  cashMax: number;
  debtMin: number;
  equityMin: number;
  cashMin: number;
  /**
   * Balance Sheet
   */
  liabilitiesBalanceSheet: BalanceSheetHierarchyNode;
  assetsBalanceSheet: BalanceSheetHierarchyNode;
}

const Health = ({
  companyId,
  isFund,
  reportingCurrencyISO,
  reportingUnit,
  sectionNumber,
  companyName,
  shortTermAssets,
  shortTermLiabilities,
  longTermAssets,
  longTermLiabilities,
  shortTermAssetsFormatted0DP,
  shortTermAssetsFormatted2DP,
  shortTermLiabilitiesFormatted0DP,
  shortTermLiabilitiesFormatted2PD,
  longTermAssetsFormatted0DP,
  longTermAssetsFormatted2DP,
  longTermLiabilitiesFormatted0DP,
  longTermLiabilitiesFormatted2DP,
  /**
   * Debt To Equity History
   */
  debt,
  equity,
  cash,
  debtMax,
  equityMax,
  cashMax,
  debtMin,
  equityMin,
  cashMin,
  /**
   * Balance Sheet
   */
  liabilitiesBalanceSheet,
  assetsBalanceSheet,
}: Props) => {
  const isPristine = useVisibleSectionsIsPristine();
  const subSectionTitleElement = isPristine ? 'h2' : 'h3';
  const [showReport, setShowReport] = useState(true);
  const tracking = useTracking();
  const trackingButtonData = {
    action: 'click',
    modifier: 'show_Meaningless_data',
  };

  const canonicalUrl = useCompanyCanonicalUrl();
  const { data: statements } = useCompanyStatementsAPIFormatted({
    canonicalURL: canonicalUrl,
  });
  const {
    AreShortTermLiabilitiesCovered: areShortTermLiabilitiesCovered,
    AreLongTermLiabilitiesCovered: areLongTermLiabilitiesCovered,
    IsDebtLevelAppropriate: isDebtLevelAppropriate,
    HasDebtReducedOverTime: hasDebtReducedOverTime,
    IsDebtCoveredByCashflow: isDebtCoveredByCashflow,
    IsInterestCoveredByProfit: isInterestCoveredByProfit,
    HasAnAppropriateLevelOfAssets: hasAnAppropriateLevelOfAssets,
    HasAppropriateBadLoanAllowance: hasAppropriateBadLoanAllowance,
    HasPrimarilyLowRiskFunding: hasPrimarilyLowRiskFunding,
    HasAppropriateLoanLevel: hasAppropriateLoanLevel,
    HasPrimarilyDepositFunding: hasPrimarilyDepositFunding,
    HasAppropriateNonPerformingLoans: hasAppropriateNonPerformingLoans,
    HasCashRunwayIfStable: hasCashRunwayIfStable,
    HasCashRunwayIfGrowing: hasCashRunwayIfGrowing,
    IsProfitable: isProfitable,
    IsProfitableLongTerm: isProfitableLongTerm,
    IsHealthAnalysisMeaningless: isHealthAnalysisMeaningless,
    IsBank: isBankStatement,
  } = statements || {};

  const isDataMeaningless = isHealthAnalysisMeaningless?.value === true;
  const isBank = isBankStatement?.value === true;

  useEffect(() => {
    setShowReport(isDataMeaningless === false);
  }, [isDataMeaningless]);

  const { netWorthDownloadUrl, historicalDebtDownloadUrl } = useMemo(() => {
    return {
      netWorthDownloadUrl: getCompanyInfographicDownloadUrl(
        companyId,
        ChartType.FinancialPositionAnalysis
      ),
      historicalDebtDownloadUrl: getCompanyInfographicDownloadUrl(
        companyId,
        ChartType.DebtEquityHistoryAnalysis
      ),
    };
  }, [companyId]);

  const isLossMaking =
    isProfitable?.value === false && isProfitableLongTerm?.value === false;

  const showCashRunwayAnalysis = isLossMaking && !isBank;

  const t = useHealthTranslations();

  const assetsVsLiabilitiesLabels = useMemo(() => {
    return {
      shortTermAssets: t('assetVsLiabilities.term1'),
      longTermAssets: t('assetVsLiabilities.term2'),
      barLabel1: t('assetsVsLiabilities.barLabel1'),
      barLabel2: t('assetsVsLiabilities.barLabel2'),
      shortTermBar1Tooltip: t('assetsVsLiabilities.term1.tooltip1'),
      shortTermBar2Tooltip: t('assetsVsLiabilities.term1.tooltip2'),
      longTermBar1Tooltip: t('assetsVsLiabilities.term2.tooltip1'),
      longTermBar2Tooltip: t('assetsVsLiabilities.term2.tooltip2'),
    };
  }, [t]);

  const debtToEquityHistoryLabels = useMemo(
    () =>
      ({
        tooltipSuffix: t('debtToEquityHistory.chart.tooltip'),
        noData: t('noData'),
        seriesLabels: [
          t('debtToEquityHistory.chart.key1'),
          t('debtToEquityHistory.chart.key2'),
          t('debtToEquityHistory.chart.key3'),
        ],
      }) as const,
    [t]
  );

  const translatedAssetsBalanceSheet = useMemo(() => {
    function getKeyFromId(id?: string) {
      if (typeof id === 'undefined') {
        return undefined;
      }
      const safeId = id.toLowerCase();
      switch (safeId) {
        case 'cash & short term investments':
          return 'balanceSheet.id.cashShortTermInvestments';
        case 'receivables':
          return 'balanceSheet.id.receivables';
        case 'inventory':
          return 'balanceSheet.id.inventory';
        case 'long term & other assets':
          return 'balanceSheet.id.longTermOtherAssets';
        case 'physical assets':
          return 'balanceSheet.id.physicalAssets';
      }
    }
    return {
      ...assetsBalanceSheet,
      children: assetsBalanceSheet.children?.map((child) => {
        const tKey = getKeyFromId(child.id);
        const remappedId = tKey ? t(tKey) : child.id;
        return {
          ...child,
          id: remappedId,
        };
      }),
    } as typeof assetsBalanceSheet;
  }, [assetsBalanceSheet, t]);

  const translatedLiabilitiesBalanceSheet = useMemo(() => {
    function getKeyFromId(id?: string) {
      if (typeof id === 'undefined') {
        return undefined;
      }
      const safeId = id.toLowerCase();
      switch (safeId) {
        case 'accounts payable':
          return 'balanceSheet.id.accountsPayable';
        case 'debt':
          return 'balanceSheet.id.debt';
        case 'equity':
          return 'balanceSheet.id.equity';
        case 'other liabilities':
          return 'balanceSheet.id.otherLiabilities';
      }
    }
    return {
      ...liabilitiesBalanceSheet,
      children: liabilitiesBalanceSheet.children?.map((child) => {
        const tKey = getKeyFromId(child.id);
        const remappedId = tKey ? t(tKey) : child.id;
        return {
          ...child,
          id: remappedId,
        };
      }),
    } as typeof liabilitiesBalanceSheet;
  }, [liabilitiesBalanceSheet, t]);
  return (
    <ReportSection sectionNumber={sectionNumber} data-cy-id="health-section">
      <CompanySectionTitle
        title={t('title')}
        pristineTitle={t('pristineTitle', { company: companyName })}
      />
      <Header />
      {isDataMeaningless && (
        <ReportSubSection
          body={
            <>
              <HealthTranslation
                i18nKey="dataIsMeaningless"
                values={{ company: companyName }}
              >
                <p>
                  In this section we usually analyse {companyName}
                  's finance health to determine how well-positioned it is
                  against times of financial distress, in particular, its
                  ability to manage its cash and debt levels. {companyName} has
                  not provided adequate balance sheet data, its financial health
                  cannot be properly assessed as it.
                </p>
                <p>
                  This is quite a rare situation as 97% of companies covered by
                  Simply Wall St do have financial information.
                </p>
              </HealthTranslation>
              {showReport === false && (
                <Button
                  styleType="louderLarge"
                  onClick={() => {
                    tracking.trackEvent(trackingButtonData);
                    setShowReport(!showReport);
                  }}
                >
                  {t('showReport')}
                </Button>
              )}
            </>
          }
        />
      )}

      {showReport && (
        <>
          <BrazeContentCardWrapper>
            <BrazeContentCard
              feedType="company-overview.financial_health"
              anchorTarget="_blank"
            />
          </BrazeContentCardWrapper>
          <PageAnchor type="sub-section-title" id={HEALTH_FINANCIAL_POSITION} />
          <ReportSubSection
            titleElement={subSectionTitleElement}
            title={t('subSection.2')}
            trackingProps={{ subject: 'chart' }}
            body={
              <ChartWrapper
                id="health-financial-position-chart"
                preRenderHeight="302px"
                chart={
                  <AssetsVsLiabilities
                    companyName={companyName}
                    shortTermAssets={shortTermAssets}
                    shortTermLiabilities={shortTermLiabilities}
                    longTermAssets={longTermAssets}
                    longTermLiabilities={longTermLiabilities}
                    shortTermAssetsFormatted0DP={shortTermAssetsFormatted0DP}
                    shortTermAssetsFormatted2DP={shortTermAssetsFormatted2DP}
                    shortTermLiabilitiesFormatted0DP={
                      shortTermLiabilitiesFormatted0DP
                    }
                    shortTermLiabilitiesFormatted2PD={
                      shortTermLiabilitiesFormatted2PD
                    }
                    longTermAssetsFormatted0DP={longTermAssetsFormatted0DP}
                    longTermAssetsFormatted2DP={longTermAssetsFormatted2DP}
                    longTermLiabilitiesFormatted0DP={
                      longTermLiabilitiesFormatted0DP
                    }
                    longTermLiabilitiesFormatted2DP={
                      longTermLiabilitiesFormatted2DP
                    }
                    labels={assetsVsLiabilitiesLabels}
                  />
                }
                feedbackLabel={COMPANY_FINANCIAL_POSITION_ANALYSIS}
                exportDarkUrl={netWorthDownloadUrl}
              />
            }
            statements={
              !isBank && (
                <>
                  <Statement
                    title={areShortTermLiabilitiesCovered?.title}
                    description={
                      <Description statement={areShortTermLiabilitiesCovered} />
                    }
                    value={areShortTermLiabilitiesCovered?.value}
                    statementId={CompanyStatements.healthStatement1}
                  />
                  <Statement
                    title={areLongTermLiabilitiesCovered?.title}
                    description={
                      <Description statement={areLongTermLiabilitiesCovered} />
                    }
                    value={areLongTermLiabilitiesCovered?.value}
                    statementId={CompanyStatements.healthStatement2}
                  />
                </>
              )
            }
          />
          <hr />
          <PageAnchor type="sub-section-title" id={HEALTH_DEBT_HISTORY} />
          <PageAnchor type="sub-section-title" id={LegacyAnchor.DEBT_EQUITY} />
          <ReportSubSection
            titleElement={subSectionTitleElement}
            title={t('subSection.3')}
            trackingProps={{ subject: 'chart' }}
            bodyRender={(title) => (
              <ChartWrapper
                id="health-debt-history-chart"
                preRenderHeight="490px"
                preRenderMinHeightSmDown="426px"
                chart={
                  <DebtToEquityHistory
                    cash={cash}
                    cashMax={cashMax}
                    cashMin={cashMin}
                    currencyISO={reportingCurrencyISO}
                    debt={debt}
                    debtMax={debtMax}
                    debtMin={debtMin}
                    equity={equity}
                    equityMax={equityMax}
                    equityMin={equityMin}
                    reportingUnit={reportingUnit}
                    labels={debtToEquityHistoryLabels}
                  />
                }
                rawDataModal={
                  <DebtToEquityHistoryRawDataContainer companyId={companyId} />
                }
                rawDataTitle={title}
                feedbackLabel={COMPANY_DEBT_TO_EQUITY_HISTORY}
                exportDarkUrl={historicalDebtDownloadUrl}
              />
            )}
            statements={
              !isBank && (
                <>
                  <Statement
                    title={isDebtLevelAppropriate?.title}
                    description={
                      <Description statement={isDebtLevelAppropriate} />
                    }
                    value={isDebtLevelAppropriate?.value}
                    statementId={CompanyStatements.healthStatement3}
                  />
                  <Statement
                    title={hasDebtReducedOverTime?.title}
                    description={
                      <Description statement={hasDebtReducedOverTime} />
                    }
                    value={hasDebtReducedOverTime?.value}
                    statementId={CompanyStatements.healthStatement4}
                  />
                  {!showCashRunwayAnalysis && (
                    <>
                      <Statement
                        title={isDebtCoveredByCashflow?.title}
                        description={
                          <Description statement={isDebtCoveredByCashflow} />
                        }
                        value={isDebtCoveredByCashflow?.value}
                        statementId={CompanyStatements.healthStatement5}
                      />
                      <Statement
                        title={isInterestCoveredByProfit?.title}
                        description={
                          <Description statement={isInterestCoveredByProfit} />
                        }
                        value={isInterestCoveredByProfit?.value}
                        statementId={CompanyStatements.healthStatement6}
                      />
                    </>
                  )}
                </>
              )
            }
          />
          <hr />
          <PageAnchor type="sub-section-title" id={HEALTH_BALANCE_SHEET} />
          <ReportSubSection
            titleElement={subSectionTitleElement}
            title={t('subSection.4')}
            body={
              <ChartWrapper
                id="health-balance-sheet-chart"
                preRenderHeight="325px"
                preRenderMinHeightMdDown="624px"
                chart={
                  <BalanceSheetWrapper>
                    <Column
                      col={12}
                      colLg={6}
                      data-cy-id="balance-sheet-assets"
                    >
                      <h4>{t('balanceSheet.assets')}</h4>
                      <BalanceSheet data={translatedAssetsBalanceSheet} />
                    </Column>
                    <Column
                      col={12}
                      colLg={6}
                      data-cy-id="balance-sheet-liabilities"
                    >
                      <h4>{t('balanceSheet.liabilitiesEquity')}</h4>
                      <BalanceSheet data={translatedLiabilitiesBalanceSheet} />
                    </Column>
                  </BalanceSheetWrapper>
                }
              />
            }
          />
          {showCashRunwayAnalysis && (
            <>
              <hr />
              <PageAnchor type="sub-section-title" id={HEALTH_CASH_RUNWAY} />
              <ReportSubSection
                titleElement={subSectionTitleElement}
                title={t('subSection.optional.4')}
                body={<p>{t('cashRunwayAnalysis.copy')}</p>}
                statements={
                  <>
                    <Statement
                      title={hasCashRunwayIfStable?.title}
                      description={
                        <Description statement={hasCashRunwayIfStable} />
                      }
                      value={hasCashRunwayIfStable?.value}
                      statementId="hasCashRunwayIfStable"
                    />
                    <Statement
                      title={hasCashRunwayIfGrowing?.title}
                      description={
                        <Description statement={hasCashRunwayIfGrowing} />
                      }
                      value={hasCashRunwayIfGrowing?.value}
                      statementId="hasCashRunwayIfGrowing"
                    />
                  </>
                }
              />
            </>
          )}
          {isBank && (
            <>
              <hr />
              <PageAnchor
                type="sub-section-title"
                id={HEALTH_FINANCIAL_INSTITUTION}
              />
              <ReportSubSection
                titleElement={subSectionTitleElement}
                title={t('subSection.optional.5')}
                trackingProps={{ subject: 'chart' }}
                statements={
                  <>
                    <Statement
                      title={hasAnAppropriateLevelOfAssets?.title}
                      description={
                        <Description
                          statement={hasAnAppropriateLevelOfAssets}
                        />
                      }
                      value={hasAnAppropriateLevelOfAssets?.value}
                      statementId={
                        CompanyStatements.alternativeHealthStatement1
                      }
                    />
                    <Statement
                      title={hasAppropriateBadLoanAllowance?.title}
                      description={
                        <Description
                          statement={hasAppropriateBadLoanAllowance}
                        />
                      }
                      value={hasAppropriateBadLoanAllowance?.value}
                      statementId={
                        CompanyStatements.alternativeHealthStatement2
                      }
                    />
                    <Statement
                      title={hasPrimarilyLowRiskFunding?.title}
                      description={
                        <Description statement={hasPrimarilyLowRiskFunding} />
                      }
                      value={hasPrimarilyLowRiskFunding?.value}
                      statementId={
                        CompanyStatements.alternativeHealthStatement3
                      }
                    />
                    <Statement
                      title={hasAppropriateLoanLevel?.title}
                      description={
                        <Description statement={hasAppropriateLoanLevel} />
                      }
                      value={hasAppropriateLoanLevel?.value}
                      statementId={
                        CompanyStatements.alternativeHealthStatement4
                      }
                    />
                    <Statement
                      title={hasPrimarilyDepositFunding?.title}
                      description={
                        <Description statement={hasPrimarilyDepositFunding} />
                      }
                      value={hasPrimarilyDepositFunding?.value}
                      statementId={
                        CompanyStatements.alternativeHealthStatement5
                      }
                    />
                    <Statement
                      title={hasAppropriateNonPerformingLoans?.title}
                      description={
                        <Description
                          statement={hasAppropriateNonPerformingLoans}
                        />
                      }
                      value={hasAppropriateNonPerformingLoans?.value}
                      statementId={
                        CompanyStatements.alternativeHealthStatement6
                      }
                    />
                  </>
                }
              />
            </>
          )}
        </>
      )}
      {isFund === false && (
        <>
          <hr />
          <NextStep section="health" title={t('subSection.discover')} />
        </>
      )}
    </ReportSection>
  );
};

export default track({ type: 'track', subject: 'Health' })(Health);
